'use client'

import { usePathname } from 'next/navigation'
import { FileText } from 'lucide-react'
import { formatDateLong } from '@/utils/util'
import { navigateUrl } from '@/utils/client-request'

type PageKey = 'delivery' | 'payment' | 'service'

const PANEL_BY_KEY: Record<PageKey, string> = {
  delivery: '#FFD9E7',
  payment: '#E8E2FF',
  service: '#FFE0C9',
}

export default function DocPage({ vendor, pageKey, title, description, htmlContent, emptyMessage }: {
  vendor?: any
  pageKey: PageKey
  title: string
  description: string
  htmlContent?: string | null
  emptyMessage: string
}) {
  const pathname = usePathname() || ''
  const updatedAt = vendor?.updatedAt || vendor?.createdAt
  const updatedLabel = updatedAt ? formatDateLong(updatedAt) : null
  const panel = PANEL_BY_KEY[pageKey]

  return (
    <section className="bg-cream pb-16">
      <div className="mx-auto max-w-[1280px] px-4 sm:px-6 lg:px-10 pt-6">
        <nav className="text-[13px] text-foreground/55 mb-6">
          <a href={navigateUrl('/', pathname)} className="hover:text-foreground">Нүүр</a>
          <span className="mx-1.5">/</span>
          <span className="text-foreground">{title}</span>
        </nav>
      </div>

      <div className="w-full py-16 sm:py-24 lg:py-32" style={{ background: panel }}>
        <div className="mx-auto max-w-[1280px] px-4 sm:px-6 lg:px-10">
          <h1 className="font-bold font-gilroy tracking-[-0.025em] leading-[0.96] text-foreground max-w-4xl" style={{ fontSize: 'clamp(2.5rem, 6.5vw, 5.5rem)' }}>
            {title}.
          </h1>
        </div>
      </div>

      <div className="mx-auto max-w-[1280px] px-4 sm:px-6 lg:px-10 mt-12">
        {updatedLabel && (
          <div className="text-[13px] text-foreground/60 mb-6">Сүүлд шинэчилсэн ({updatedLabel})</div>
        )}
        <p className="text-[16px] sm:text-[17px] leading-[1.75] text-foreground/80 mb-10">
          {description}
        </p>

        {htmlContent ? (
          <div
            className="doc-prose text-[16px] sm:text-[17px] leading-[1.75] text-foreground/85"
            dangerouslySetInnerHTML={{ __html: htmlContent }}
          />
        ) : (
          <div className="rounded-2xl border border-dashed border-foreground/15 bg-[#F1EEE7]/40 p-10 text-center">
            <FileText className="size-10 text-foreground/40 mx-auto mb-3" />
            <p className="text-[14px] text-foreground/55">{emptyMessage}</p>
          </div>
        )}
      </div>
    </section>
  )
}
